const POLL_INTERVAL_MS = 2000;
const PROBE_TIMEOUT_MS = 3000;
// A public connectivity endpoint may drop an occasional request. The interview
// WebSocket heartbeat remains authoritative, so require consecutive failures
// before treating this advisory probe as an outage.
const CONNECTION_FAILURE_THRESHOLD = 3;

// A loopback WebSocket can remain healthy when the candidate loses internet.
export const monitorInterviewConnectivity = ({
  probeUrl,
  probeInternet = false,
  initialReachable = true,
  onChange,
}) => {
  // A probe detects internet reachability; the interview socket confirms resume.
  const checkInternet = probeInternet && Boolean(probeUrl);

  let reachable = initialReachable;
  let failures = 0;
  let disposed = false;
  let controller = null;
  let probeTimeout = null;
  let generation = 0;
  let onlineEventOverride = false;

  const notify = (next, reason = "unknown", force = false) => {
    if (disposed || (!force && reachable === next)) return;

    reachable = next;
    onChange(next, { reason });
  };

  const cancelProbe = () => {
    generation += 1;

    clearTimeout(probeTimeout);
    probeTimeout = null;

    controller?.abort();
    controller = null;
  };

  const check = async () => {
    if (disposed) return;

    const browserReportsOffline = navigator.onLine === false;

    if (!checkInternet) {
      if (!browserReportsOffline) {
        onlineEventOverride = false;
      }
      notify(
        !browserReportsOffline || onlineEventOverride,
        browserReportsOffline && !onlineEventOverride
          ? "browser_offline"
          : "browser_online",
      );
      return;
    }

    // Prevent overlapping connectivity probes.
    if (controller) return;

    const request = new AbortController();

    controller = request;

    const requestGeneration = generation;

    try {
      const url = new URL(probeUrl, window.location.href);
      url.searchParams.set("connection_check", `${Date.now()}-${generation}`);
      const requestPromise = fetch(url.href, {
        method: "GET",
        mode: "no-cors",
        cache: "no-store",
        credentials: "omit",
        referrerPolicy: "no-referrer",
        signal: request.signal,
      });

      /*
       * Bound the connectivity check even if a browser request
       * does not settle correctly after abort().
       */
      const timeout = new Promise((resolve, reject) => {
        probeTimeout = setTimeout(() => {
          request.abort();
          reject(new Error("Connectivity check timed out"));
        }, PROBE_TIMEOUT_MS);
      });

      // One successful probe is enough to start reconnecting; the interview
      // WebSocket still has to confirm that the interview server is usable.
      await Promise.race([requestPromise, timeout]);

      if (
        disposed ||
        requestGeneration !== generation
      ) {
        return;
      }

      failures = 0;
      if (reachable) {
        return;
      }
      notify(true, "probe_success");
    } catch (error) {
      if (
        disposed ||
        requestGeneration !== generation
      ) {
        return;
      }

      failures += 1;

      /*
       * Surface a failed probe immediately so interrupted speech/listening
       * cannot remain displayed while the interview socket is half-open.
       */
      if (failures >= CONNECTION_FAILURE_THRESHOLD) {
        notify(false, "probe_failure");
      }
    } finally {
      if (requestGeneration === generation) {
        clearTimeout(probeTimeout);
        probeTimeout = null;

        controller = null;

        request.abort();
      }
    }
  };

  /*
   * Browser explicitly reports that the network went offline.
   *
   * We immediately notify the application, but the polling probe
   * will continue after this so recovery does not depend solely
   * on the browser firing the "online" event.
   */
  const offline = () => {
    onlineEventOverride = false;
    cancelProbe();

    failures = 0;

    notify(false, "browser_offline");
  };

  const online = () => {
    onlineEventOverride = true;
    if (checkInternet) {
      notify(true, "browser_online", true);
      check();
      return;
    }

    failures = 0;
    notify(true, "browser_online");
  };

  window.addEventListener("offline", offline);
  window.addEventListener("online", online);

  // Recheck the browser's network hint in case an offline/online event is
  // missed. This does not issue network requests; the interview heartbeat
  // remains the authority for confirming that the server is usable.
  const interval = setInterval(check, POLL_INTERVAL_MS);

  if (checkInternet) {
    check();
  } else {
    notify(
      navigator.onLine !== false,
      navigator.onLine === false ? "browser_offline" : "browser_online",
    );
  }

  return () => {
    disposed = true;

    cancelProbe();

    clearInterval(interval);

    window.removeEventListener("offline", offline);
    window.removeEventListener("online", online);
  };
};
